<template>
  <div>
    <base-header type="instore" class="pb-6 pb-8 pt-5 pt-md-8 eod-sales-header">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-4">
        <div>
          <h2 class="text-dark mb-1">EOD Monitoring Dashboard</h2>
          <p class="text-muted mb-0">Track terminal reconciliation from the Dashboard section.</p>
        </div>
        <a-button type="default" @click="refreshDashboard">
          <a-icon type="reload" />
        </a-button>
      </div>

      <div class="row">
        <template v-if="eodDashboardMode == 'classic'">
          <div class="col-xl-2 col-lg-4 col-md-6">
            <a-spin :spinning="loadingEodDashboard">
              <stats-card title="PENDING" type="gradient-orange" :sub-title="pendingStats" icon="fa fa-solid fa-hourglass-half"
                class="mb-4 mb-xl-0">
                <template slot="footer">
                  <span class="mr-2"> Pending Terminals </span>
                  <br><br>
                </template>
              </stats-card>
            </a-spin>
          </div>
          <div class="col-xl-2 col-lg-4 col-md-6">
            <a-spin :spinning="loadingEodDashboard">
              <stats-card title="NO ACTIVITY" type="gradient-default" :sub-title="noActivityStats" icon="fa fa-solid fa-power-off"
                class="mb-4 mb-xl-0">
                <template slot="footer">
                  <span class="mr-2"> Offline / No Activity </span>
                  <br><br>
                </template>
              </stats-card>
            </a-spin>
          </div>
          <div class="col-xl-2 col-lg-4 col-md-6">
            <a-spin :spinning="loadingEodDashboard">
              <stats-card title="COMPLETED" type="gradient-green" :sub-title="completedStats" icon="fa fa-solid fa-clipboard-check"
                class="mb-4 mb-xl-0">
                <template slot="footer">
                  <span class="mr-2"> Completed Terminals </span>
                  <br><br>
                </template>
              </stats-card>
            </a-spin>
          </div>
          <div class="col-xl-2 col-lg-6 col-md-6">
            <a-spin :spinning="loadingEodDashboard">
              <stats-card title="DISCREPANCY" type="gradient-red" :sub-title="errorStats" icon="fa fa-solid fa-circle-xmark"
                class="mb-4 mb-xl-0">
                <template slot="footer">
                  <span class="mr-2"> Terminals with Discrepancies </span>
                  <br><br>
                </template>
              </stats-card>
            </a-spin>
          </div>
          <div class="col-xl-4 col-lg-6 col-md-12">
            <a-spin :spinning="loadingEodDashboard">
              <stats-card class="mb-4 mb-xl-0 eod-progress-card" style="background:#2b2b2b; color: white;" type="gradient-blue" sub-title="-" icon="fa fa-solid fa-chart-pie">
                <span class="card-title text-uppercase mb-0 h5 eod-progress-title">OVERALL PROGRESS</span>
                <br />
                <a-progress
                  :percent="progressPercent"
                  status="active"
                  :strokeWidth="25"
                  stroke-color="#d9d9d9"
                  stroke-linecap="square"
                />
                <template slot="footer">
                  <span class="mr-2 eod-progress-footer"> Total Terminals / Completed Terminals </span>
                  <br>
                </template>
              </stats-card>
            </a-spin>
          </div>
        </template>
        <template v-else>
          <div class="col-12">
            <div class="row eod-summary-top-row">
              <div class="col-xl-6 col-lg-12">
                <a-spin :spinning="loadingEodDashboard">
                  <div class="card border-0 shadow-sm mb-4 eod-hero-summary">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-start flex-wrap mb-4">
                        <div class="eod-hero-heading">
                          <span class="eod-stat-overline">EOD Coverage</span>
                          <h2 class="mb-2">{{ progressPercent }}%</h2>
                          <p class="mb-0 eod-hero-copy">{{ eodSummary.completed_count || 0 }} of {{ eodSummary.total_terminal_count || 0 }} terminals reconciled</p>
                        </div>
                        <div class="eod-hero-chip">
                          <span>Needs Review</span>
                          <strong>{{ eodSummary.discrepancy_count || 0 }}</strong>
                        </div>
                      </div>
                      <a-progress
                        :percent="progressPercent"
                        status="active"
                        :strokeWidth="18"
                        stroke-color="#f8fafc"
                        stroke-linecap="square"
                      />
                      <div class="eod-hero-pills mt-4">
                        <div class="eod-hero-pill">
                          <span>Active Terminals</span>
                          <strong>{{ eodSummary.total_terminal_count || 0 }}</strong>
                        </div>
                        <div class="eod-hero-pill">
                          <span>Completed</span>
                          <strong>{{ eodSummary.completed_count || 0 }}</strong>
                        </div>
                        <div class="eod-hero-pill">
                          <span>Open Items</span>
                          <strong>{{ eodSummary.discrepancy_count || 0 }}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-spin>
              </div>
              <div class="col-xl-2 col-lg-6 col-md-6">
                <a-spin :spinning="loadingEodDashboard">
                  <div class="card border-0 shadow-sm mb-4 eod-stat-card eod-top-stat-card is-pending-card">
                    <div class="card-body">
                      <div class="eod-stat-icon"><i class="fa fa-solid fa-hourglass-half"></i></div>
                      <span class="eod-stat-overline">Pending Z-read</span>
                      <h2>{{ eodSummary.pending_zread_count || 0 }}</h2>
                      <p class="eod-stat-copy">POS activity exists, but no submitted Z-reading yet for the selected business date.</p>
                    </div>
                  </div>
                </a-spin>
              </div>
              <div class="col-xl-2 col-lg-6 col-md-6">
                <a-spin :spinning="loadingEodDashboard">
                  <div class="card border-0 shadow-sm mb-4 eod-stat-card eod-top-stat-card is-no-activity-card">
                    <div class="card-body">
                      <div class="eod-stat-icon"><i class="fa fa-solid fa-power-off"></i></div>
                      <span class="eod-stat-overline">No Activity</span>
                      <h2>{{ eodSummary.no_activity_count || 0 }}</h2>
                      <p class="eod-stat-copy">No POS activity was recorded for the selected business date.</p>
                    </div>
                  </div>
                </a-spin>
              </div>
              <div class="col-xl-2 col-lg-6 col-md-6">
                <a-spin :spinning="loadingEodDashboard">
                  <div class="card border-0 shadow-sm mb-4 eod-stat-card eod-top-stat-card is-complete-card">
                    <div class="card-body">
                      <div class="eod-stat-icon"><i class="fa fa-solid fa-clipboard-check"></i></div>
                      <span class="eod-stat-overline">Fully Reconciled</span>
                      <h2>{{ eodSummary.completed_count || 0 }}</h2>
                      <p class="eod-stat-copy">Count, gross, and net sales all line up for the day.</p>
                    </div>
                  </div>
                </a-spin>
              </div>
            </div>
            <div class="row eod-summary-bottom-row">
              <div class="col-xl-4 col-lg-4 col-md-6">
                <a-spin :spinning="loadingEodDashboard">
                  <div class="card border-0 shadow-sm mb-4 eod-stat-card is-count-card">
                    <div class="card-body">
                      <div class="eod-stat-icon"><i class="fa fa-solid fa-list-ol"></i></div>
                      <span class="eod-stat-overline">Count Issues</span>
                      <h2>{{ eodSummary.count_issue_count || 0 }}</h2>
                      <p class="eod-stat-copy">Transaction completeness breaks between Z-read and POS documents.</p>
                    </div>
                  </div>
                </a-spin>
              </div>
              <div class="col-xl-4 col-lg-4 col-md-6">
                <a-spin :spinning="loadingEodDashboard">
                  <div class="card border-0 shadow-sm mb-4 eod-stat-card is-gross-card">
                    <div class="card-body">
                      <div class="eod-stat-icon"><i class="fa fa-solid fa-coins"></i></div>
                      <span class="eod-stat-overline">Gross Issues</span>
                      <h2>{{ eodSummary.gross_issue_count || 0 }}</h2>
                      <p class="eod-stat-copy">Gross sales do not reconcile between Z-read and POS documents.</p>
                    </div>
                  </div>
                </a-spin>
              </div>
              <div class="col-xl-4 col-lg-4 col-md-6">
                <a-spin :spinning="loadingEodDashboard">
                  <div class="card border-0 shadow-sm mb-4 eod-stat-card is-net-card">
                    <div class="card-body">
                      <div class="eod-stat-icon"><i class="fa fa-solid fa-scale-balanced"></i></div>
                      <span class="eod-stat-overline">Net Issues</span>
                      <h2>{{ eodSummary.net_issue_count || 0 }}</h2>
                      <p class="eod-stat-copy">Net sales still need investigation for the selected date.</p>
                    </div>
                  </div>
                </a-spin>
              </div>
            </div>
          </div>
        </template>
      </div>
    </base-header>

    <div class="container-fluid mt--7">
      <div v-if="showSiteRollup" class="row mb-4">
        <div class="col">
          <a-spin :spinning="loadingSiteRollup">
            <div class="card border-0 shadow-sm eod-site-breakdown-card">
              <div class="card-header border-0 d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <span class="eod-stat-overline mb-2">All Sites</span>
                  <h3 class="mb-1">Per-site Breakdown</h3>
                  <p class="text-muted mb-0">Rolled up terminal reconciliation for {{ filters.businessDate || '-' }}.</p>
                </div>
              </div>

              <div v-if="siteRollupRows.length > 0" class="table-responsive">
                <table class="table table-sm align-items-center mb-0 eod-site-breakdown-table">
                  <thead class="thead-light">
                    <tr>
                      <th>Site</th>
                      <th>Coverage</th>
                      <th class="text-center">Pending Z-read</th>
                      <th class="text-center eod-no-activity-cell">No Activity</th>
                      <th class="text-center">Exceptions</th>
                      <th class="text-center">Count Issues</th>
                      <th class="text-center">Gross Issues</th>
                      <th class="text-center">Net Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="siteRow in siteRollupRows" :key="siteRow.site_id || siteRow.site_code || siteRow.site_name">
                      <td>
                        <div class="font-weight-600">{{ siteRow.site_name || siteRow.site_code || 'Unknown Site' }}</div>
                        <div class="small text-muted">{{ integerValue(siteRow.total_terminal_count) }} active terminals</div>
                      </td>
                      <td>
                        <div class="font-weight-600">{{ siteProgressPercent(siteRow) }}%</div>
                        <div class="small text-muted">
                          {{ integerValue(siteRow.completed_count) }} / {{ integerValue(siteRow.total_terminal_count) }} completed
                        </div>
                      </td>
                      <td class="text-center">{{ integerValue(siteRow.pending_zread_count) }}</td>
                      <td class="text-center eod-no-activity-cell">{{ integerValue(siteRow.no_activity_count) }}</td>
                      <td class="text-center">{{ integerValue(siteRow.exception_count) }}</td>
                      <td class="text-center">{{ integerValue(siteRow.count_issue_count) }}</td>
                      <td class="text-center">{{ integerValue(siteRow.gross_issue_count) }}</td>
                      <td class="text-center">{{ integerValue(siteRow.net_issue_count) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-else class="card-body text-center text-muted py-4">
                No site breakdown is available for the selected filters.
              </div>
            </div>
          </a-spin>
        </div>
      </div>

      <div class="row">
        <div class="col">
          <div class="mb-3 card dashboard-shell">
            <template v-if="eodDashboardMode == 'classic'">
              <div class="row no-gutters">
                <div class="col-md-3" style="border-right: 1px solid #e0e0e0">
                  <div class="card-body">
                    <div class="filter-bar ui basic segment grid">
                      <div class="ui form">
                        <div class="inline field">
                          <h6 class="heading-small text-muted mb-4">Search</h6>
                          <div class="input-group">
                            <div class="input-group-append">
                              <base-input alternative="" label="Business Date">
                                <br>
                                <a-date-picker :defaultValue="moment()" v-model="inputBusinessDate" :format="dateFormat" @change="formatDate" />
                              </base-input>
                            </div>
                          </div>
                          <div class="input-group ">
                            <div class="input-group-append">
                              <base-input alternative="" label="Site" inputClasses="w-100">
                                <a-select v-model="filters.siteId" @change="fetchTerminals" class="w-100" :disabled="isBranch">
                                  <a-select-option key="0" value="0">All Sites</a-select-option>
                                  <a-select-option v-for="site in sites" :key="site.code" :value="site.id">
                                    {{ site.name }}
                                  </a-select-option>
                                </a-select>
                              </base-input>
                            </div>
                          </div>
                          <div class="input-group ">
                            <div class="input-group-append">
                              <base-input alternative="" label="Terminal">
                                <a-select v-model="filters.terminalId" class="w-100" mode="multiple">
                                  <a-select-option v-for="terminal in terminals" :key="terminal.code" :value="terminal.id">
                                    {{ terminal.name }}
                                  </a-select-option>
                                </a-select>
                              </base-input>
                            </div>
                          </div>

                          <hr class="my-4" />
                          <h6 class="heading-small text-muted mb-4">Filter by status</h6>
                          <div>
                            <base-radio name="0" class="mb-3" v-model="filters.status" value="O">
                              All
                            </base-radio>
                            <base-radio name="C" class="mb-3" v-model="filters.status" value="C">
                              Completed
                            </base-radio>
                            <base-radio name="P" class="mb-3" v-model="filters.status" value="P">
                              Pending
                            </base-radio>
                            <base-radio name="N" class="mb-3" v-model="filters.status" value="N">
                              No Activity
                            </base-radio>
                            <base-radio name="E" class="mb-3" v-model="filters.status" value="E">
                              Mismatch
                            </base-radio>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-9">
                  <div class="card-body">
                    <eod-monitoring-table></eod-monitoring-table>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="card-body">
                <eod-monitoring-table-new
                  :end-point="endPoint"
                  :filters="filters"
                  :summary="eodSummary"
                  :sites="sites"
                  :terminals="terminals"
                  :is-branch="isBranch"
                  :loading-summary="loadingEodDashboard"
                  @apply-filters="applyNewEodFilters"
                  @site-change="handleNewEodSiteChange"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HTTPRequest from '../controllers/httpRequest.js'
import secureStorage from './../controllers/secureStorage';
import { mapState, mapActions } from 'vuex'
import moment from 'moment';
import EodMonitoringTable from './Tables/EodMonitoringTable.vue'
import EodMonitoringTableNew from './Tables/EodMonitoringTableNew.vue'

export default {
  name: 'EodMonitoringDashboard',
  components: {
    EodMonitoringTable,
    EodMonitoringTableNew,
  },
  data() {
    const currentSiteType = secureStorage.getItem("currentSiteSiteType");
    const isBranch = currentSiteType == 'branch';
    return {
      loadingEodDashboard: false,
      loadingSiteRollup: false,
      eodDashboardMode: 'new',
      eodDashboardSettingId: null,
      eodSummary: {},
      siteRollupRows: [],
      sites: [],
      terminals: [],
      dateFormat: 'MM-DD-YYYY',
      inputBusinessDate: moment(),
      completedStats: '0',
      pendingStats: '0',
      noActivityStats: '0',
      errorStats: '0',
      progressPercent: 0,
      filters: {
        businessDate: '',
        siteId: isBranch ? secureStorage.getItem("defaultSiteId") : '0',
        terminalId: [],
        status: 'O',
        issueFilter: '',
        mismatchOnly: false,
      },
      isBranch,
    }
  },
  computed: {
    ...mapState(['currentProfile', 'parameters', 'endPoint']),
    showSiteRollup() {
      return !this.isBranch && (!this.filters.siteId || String(this.filters.siteId) === '0');
    }
  },
  watch: {
    filters: {
      handler() {
        this.changeFilter(this.filters);
      },
      deep: true
    },
    parameters() {
      this.loadEodMonitoringDashboard();
    }
  },
  async mounted() {
    this.updateCurrentProfile('eod_monitoring_dashboards');
    await this.loadEodDashboardPreference();
    this.sites = await HTTPRequest.getRequest(this.endPoint + "sites/get_valid_sites?user_id=" + secureStorage.getItem("userId"), null);
    await this.fetchTerminals(this.filters.siteId);
    this.formatDate();
    this.changeFilter(this.filters);
  },
  methods: {
    ...mapActions(['updateCurrentProfile', 'updateParameters']),
    moment,
    changeFilter(filters) {
      let paramText = '';
      const appendParam = (key, value) => {
        if (value === undefined || value === null || value === '' || value === '0' || value === 'O') return;
        paramText += paramText.length ? '&' : '?';
        paramText += `${key}=${value}`;
      };

      appendParam('business_date', filters.businessDate);
      appendParam('site_id', filters.siteId);

      if (Array.isArray(filters.terminalId) && filters.terminalId.length > 0) {
        appendParam('terminal_id', filters.terminalId.join(','));
      }

      appendParam('status_filter', filters.status);
      appendParam('issue_filter', filters.issueFilter);
      if (filters.mismatchOnly) {
        appendParam('mismatch_only', 'true');
      }

      this.updateParameters(paramText);
    },
    async loadEodDashboardPreference() {
      const userId = secureStorage.getItem("userId")
      const response = await HTTPRequest.getRequest(this.endPoint + "user_settings?module_name=eod_monitoring_dashboard&user_id=" + userId, null)
      const setting = response && response.data && response.data[0] ? response.data[0] : null

      if (!setting) {
        this.eodDashboardMode = 'new'
        this.eodDashboardSettingId = null
        return
      }

      let settingsJson = setting.settings_json
      if (typeof settingsJson === 'string') {
        settingsJson = JSON.parse(settingsJson || '{}')
      }

      this.eodDashboardMode = settingsJson && settingsJson.mode === 'new' ? 'new' : 'classic'
      this.eodDashboardSettingId = setting.id
    },
    applyNewEodFilters(nextFilters) {
      if (nextFilters.businessDate) {
        this.inputBusinessDate = moment(nextFilters.businessDate)
      }

      this.filters = {
        ...this.filters,
        businessDate: nextFilters.businessDate,
        siteId: nextFilters.siteId,
        terminalId: Array.isArray(nextFilters.terminalId) ? [...nextFilters.terminalId] : [],
        status: nextFilters.status,
        issueFilter: nextFilters.issueFilter,
        mismatchOnly: nextFilters.mismatchOnly
      }

      this.fetchTerminals(nextFilters.siteId, false)
      this.loadEodMonitoringDashboard()
    },
    handleNewEodSiteChange(siteId) {
      this.fetchTerminals(siteId, false)
    },
    async loadEodMonitoringDashboard() {
      if (!this.filters.businessDate) {
        this.completedStats = '0';
        this.pendingStats = '0';
        this.noActivityStats = '0';
        this.errorStats = '0';
        this.progressPercent = 0;
        this.eodSummary = {};
        this.siteRollupRows = [];
        return;
      }

      this.loadingEodDashboard = true;
      let url = `${this.endPoint}z_readings/pos_document_reconciliation_summary?business_date=${this.filters.businessDate}`;
      if (this.filters.siteId && this.filters.siteId !== '0') {
        url += `&site_id=${this.filters.siteId}`;
      }
      if (Array.isArray(this.filters.terminalId) && this.filters.terminalId.length > 0) {
        url += `&terminal_id=${this.filters.terminalId.join(',')}`;
      }

      this.loadingSiteRollup = this.showSiteRollup;

      try {
        const requests = [HTTPRequest.getRequest(url, null)];
        if (this.showSiteRollup) {
          requests.push(HTTPRequest.getRequest(this.siteRollupUrl(), null));
        }

        const [summary, siteRollupResponse] = await Promise.all(requests);

        this.eodSummary = summary || {};
        this.completedStats = `${summary.completed_count || 0}`;
        this.pendingStats = `${summary.pending_zread_count || summary.pending_count || 0}`;
        this.noActivityStats = `${summary.no_activity_count || 0}`;
        this.errorStats = `${summary.discrepancy_count || 0}`;
        const totalCount = Number(summary.total_terminal_count || summary.total_count || 0);
        const completedCount = Number(summary.completed_count || 0);
        this.progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
        this.siteRollupRows = this.showSiteRollup && Array.isArray(siteRollupResponse) ? siteRollupResponse : [];
      } finally {
        this.loadingEodDashboard = false;
        this.loadingSiteRollup = false;
      }
    },
    siteRollupUrl() {
      const params = [`business_date=${this.filters.businessDate}`];

      if (this.filters.siteId && this.filters.siteId !== '0') {
        params.push(`site_id=${this.filters.siteId}`);
      }
      if (Array.isArray(this.filters.terminalId) && this.filters.terminalId.length > 0) {
        params.push(`terminal_id=${this.filters.terminalId.join(',')}`);
      }
      if (this.filters.status && this.filters.status !== 'O') {
        params.push(`status_filter=${this.filters.status}`);
      }
      if (this.filters.issueFilter) {
        params.push(`issue_filter=${this.filters.issueFilter}`);
      }
      if (this.filters.mismatchOnly) {
        params.push('mismatch_only=true');
      }

      return `${this.endPoint}z_readings/pos_document_reconciliation_site_rollup?${params.join('&')}`;
    },
    async fetchTerminals(siteId = this.filters.siteId, resetSharedFilters = true) {
      if (resetSharedFilters) {
        this.filters.terminalId = []
      }
      this.terminals = await HTTPRequest.getRequest(this.endPoint + "terminals/get_valid_terminals?site_id=" + siteId, null);
    },
    formatDate() {
      if (this.inputBusinessDate != null) {
        this.filters.businessDate = moment(this.inputBusinessDate).format('YYYY-MM-DD')
      } else {
        this.filters.businessDate = ''
      }
    },
    async refreshDashboard() {
      await this.fetchTerminals(this.filters.siteId, false)
      this.changeFilter(this.filters)
      this.loadEodMonitoringDashboard()
    },
    integerValue(value) {
      return Number(value || 0);
    },
    siteProgressPercent(siteRow) {
      const total = this.integerValue(siteRow.total_terminal_count);
      const completed = this.integerValue(siteRow.completed_count);
      return total > 0 ? Math.round((completed / total) * 100) : 0;
    }
  }
}
</script>

<style scoped>
.dashboard-shell {
  min-height: 70vh;
  overflow: scroll;
}

.eod-sales-header {
  background-image: none !important;
  background: #f4f7fb !important;
}

.eod-sales-header::before,
.eod-sales-header::after {
  background-image: none !important;
}

.eod-summary-top-row {
  margin-bottom: 6px;
}

.eod-site-breakdown-card {
  background: #ffffff;
}

.eod-site-breakdown-table td,
.eod-site-breakdown-table th {
  vertical-align: middle;
}

.eod-summary-bottom-row {
  margin-top: 6px;
}

.eod-hero-summary {
  min-height: 236px;
  background: #ffffff;
  border: 1px solid #dbe5f0;
  border-radius: 10px;
  color: #1f3d6c;
}

.eod-stat-overline {
  display: block;
  margin-bottom: 6px;
  color: #7c8eaa;
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.eod-hero-copy {
  color: #8aa0c2;
  font-size: 0.98rem;
  line-height: 1.45;
  max-width: 360px;
}

.eod-hero-heading {
  padding-right: 16px;
}

.eod-hero-chip {
  min-width: 150px;
  padding: 12px 16px;
  border-radius: 10px;
  background: #f7f9fc;
  border: 1px solid #e6edf5;
}

.eod-hero-chip span {
  display: block;
  color: #7c8eaa;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
}

.eod-hero-chip strong {
  display: block;
  margin-top: 4px;
  color: #1f3d6c;
  font-size: 1.5rem;
  font-weight: 800;
}

.eod-hero-pills {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.eod-hero-pill {
  padding: 14px 16px;
  border-radius: 10px;
  background: #f7f9fc;
  border: 1px solid #e6edf5;
}

.eod-hero-pill span {
  display: block;
  color: #7c8eaa;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
}

.eod-hero-pill strong {
  display: block;
  margin-top: 6px;
  color: #1f3d6c;
  font-size: 1.35rem;
  font-weight: 800;
}

.eod-stat-card {
  min-height: 196px;
  border: 1px solid #dbe5f0;
  border-radius: 10px;
  background: #ffffff;
}

.eod-stat-card .card-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 14px;
  min-height: 100%;
  padding: 22px 22px 18px;
}

.eod-top-stat-card {
  min-height: 236px;
}

.eod-stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  background: #f7f9fc;
  color: #1f3d6c;
}

.eod-stat-card h2 {
  margin: 0;
  color: #1f3d6c;
  font-size: clamp(1.8rem, 2vw, 2.3rem);
  font-weight: 800;
  line-height: 1.1;
}

.eod-stat-copy {
  color: #8aa0c2;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
  max-width: 260px;
}

.is-pending-card {
  box-shadow: inset 0 4px 0 #f59e0b;
}

.is-complete-card {
  box-shadow: inset 0 4px 0 #22c55e;
}

.is-no-activity-card {
  box-shadow: inset 0 4px 0 #6b7280;
}

.is-count-card {
  box-shadow: inset 0 4px 0 #3b82f6;
}

.is-gross-card {
  box-shadow: inset 0 4px 0 #ef4444;
}

.is-net-card {
  box-shadow: inset 0 4px 0 #eab308;
}

.eod-no-activity-cell {
  color: #6b7280;
  font-weight: 700;
}

.eod-progress-title,
.eod-progress-footer {
  color: #f5f5f5;
}

.eod-progress-card ::v-deep .ant-progress-inner {
  border-radius: 0 !important;
}

.eod-progress-card ::v-deep .ant-progress-text {
  color: #f5f5f5 !important;
  font-weight: 600;
  letter-spacing: 0.5px;
}

@media (max-width: 991px) {
  .eod-hero-pills {
    grid-template-columns: 1fr;
  }

  .eod-hero-summary,
  .eod-stat-card {
    min-height: auto;
  }
}

.ant-calendar-picker, .input-group-append, .form-group {
  width: 100%;
}
</style>
